/* eslint-disable max-lines */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { compact, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { createCatalogDrugApi, listCatalogFormOptionsApi, listDrugCodeMappingsApi } from 'api/drug';
import {
  drugCodeAllowedValueRegex,
  drugNameAllowedCharRegex,
  drugNameAllowedCharsHint,
  drugNameAllowedValueRegex,
} from 'common/constants/regex';
import { loadCatalog } from 'store/thunks/catalogThunks';
import { handleError } from 'store/thunks/errorHandlerThunks';
import CatalogSubmitBlockersHint from 'modules/StockManagement/components/CatalogSubmitBlockersHint';
import ManualProductIdField, {
  ManualProductIdInstructions,
} from 'modules/StockManagement/components/ManualProductIdField';
import {
  DRUG_CODE_FORMAT_HINT,
  listCatalogSubmitBlockers,
} from 'modules/StockManagement/utils/catalogSubmitBlockers';
import {
  describeManualProductIdConflict,
  flagVariantsNeedingManualProductId,
  handleManualProductIdError,
  resolveManualProductId,
  toManualProductIdInput,
} from 'modules/StockManagement/utils/manualProductId';
import {
  isDrugCodeTaken,
  resolveDrugCodeForName,
  toDrugCodeIndex,
} from 'modules/StockManagement/utils/drugCodeGenerator';

import CatalogDialogHeader from './CatalogDialogHeader';

const DRUG_CODE_DEBOUNCE_MS = 400;

// e-scribe-ui's shared TextField wrapper always renders a helper-text line --
// real text, or a single space as a fallback -- so every field reserves the
// same bottom space regardless of whether it has a message. Without this, a
// field with no helperText renders shorter than one that has it, and the row
// drifts out of alignment.
const RESERVED_HELPER_TEXT = ' ';
const HELPER_TEXT_PROPS = { sx: { minHeight: '1.6em', mb: 1 } };

const emptyVariant = () => ({
  strength: '',
  formAlias: '',
  packSizeValue: '',
  packSizeUnit: '',
  isCompoundingEligible: false,
  // POM-6514: set on the variants a generation failure names; manualProductIdCode is the drug
  // code + strength code the admin then types for it.
  needsManualProductId: false,
  manualProductIdCode: '',
});

const findShortCode = (formOptions, formAlias) =>
  formOptions.find(({ formAlias: alias }) => alias === formAlias)?.shortCode ?? '';

const sortAlpha = (values) =>
  [...values].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));

// One variant's fields, matching e-scribe-ui's VariantRow: Strength + Compounding
// Drug, then Form + Pack Size Unit, then Pack Size Value on its own row.
const VariantRow = ({
  index,
  variant,
  onChange,
  onRemove,
  canRemove,
  formOptions,
  loadingForms,
  formsError,
  submitted,
  manualProductIdConflict,
  isManualProductIdChecked,
  autoFocusProductId,
  drugCode,
}) => {
  const sortedFormAliases = useMemo(
    () => sortAlpha(formOptions.map(({ formAlias: alias }) => alias)),
    [formOptions]
  );

  const unitsForSelectedForm = useMemo(() => {
    const selected = formOptions.find(({ formAlias: alias }) => alias === variant.formAlias);
    return sortAlpha(selected?.units ?? []);
  }, [formOptions, variant.formAlias]);

  const handleFormAliasChange = (event) => {
    onChange(index, { formAlias: event.target.value, packSizeUnit: '' });
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1.5 }}>
        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
          Variant #{index + 1}
        </Typography>
        {canRemove && (
          <IconButton
            size='small'
            onClick={() => onRemove(index)}
            aria-label='Remove variant'
            sx={{ color: 'error.main' }}
          >
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        )}
      </Stack>

      {/* Plain flex rows with `flex: 1` on every child, rather than Grid: Grid's
          columnSpacing negative-margin math left the second column of each row
          narrower than the first, and Pack Size Value alone in its row filled the
          full width instead of matching its siblings' half-width. */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          id={`NewCatalogDialog-strength${index}-input`}
          label='Strength'
          required
          sx={{ flex: 1, mt: 1 }}
          placeholder='e.g. 250 mg'
          value={variant.strength}
          onChange={(event) => onChange(index, { strength: event.target.value })}
          error={submitted && variant.strength.trim().length === 0}
          helperText={RESERVED_HELPER_TEXT}
          FormHelperTextProps={HELPER_TEXT_PROPS}
        />
        <FormControl variant='outlined' sx={{ flex: 1, mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={variant.isCompoundingEligible}
                onChange={(event) =>
                  onChange(index, { isCompoundingEligible: event.target.checked })
                }
              />
            }
            label='Compounding Drug'
            sx={{
              ml: 0,
              border: '1px solid',
              borderColor: variant.isCompoundingEligible ? 'primary.main' : 'divider',
              borderRadius: 1,
              px: 2,
              height: '56px',
              width: '100%',
            }}
          />
          <FormHelperText sx={{ minHeight: '1.6em', mb: 1, visibility: 'hidden' }}>
            placeholder
          </FormHelperText>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField
          id={`NewCatalogDialog-formAlias${index}-input`}
          select
          label='Form'
          required
          sx={{ flex: 1 }}
          value={variant.formAlias}
          onChange={handleFormAliasChange}
          disabled={loadingForms || formsError}
          error={(submitted && variant.formAlias.length === 0) || formsError}
          helperText={
            formsError
              ? 'Could not load the form list. Close and reopen to retry.'
              : RESERVED_HELPER_TEXT
          }
          FormHelperTextProps={HELPER_TEXT_PROPS}
        >
          {sortedFormAliases.map((alias) => (
            <MenuItem key={alias} value={alias}>
              {alias}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id={`NewCatalogDialog-packSizeUnit${index}-input`}
          select
          label='Pack Size Unit'
          sx={{ flex: 1 }}
          value={variant.packSizeUnit}
          onChange={(event) => onChange(index, { packSizeUnit: event.target.value })}
          disabled={unitsForSelectedForm.length === 0}
          helperText={RESERVED_HELPER_TEXT}
          FormHelperTextProps={HELPER_TEXT_PROPS}
        >
          {unitsForSelectedForm.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField
          id={`NewCatalogDialog-packSizeValue${index}-input`}
          label='Pack Size Value'
          sx={{ flex: 1 }}
          placeholder='e.g. 20'
          value={variant.packSizeValue}
          onChange={(event) => onChange(index, { packSizeValue: event.target.value })}
          helperText={RESERVED_HELPER_TEXT}
          FormHelperTextProps={HELPER_TEXT_PROPS}
        />
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* POM-6514: its own row, with the warning right above it, so a field that only appears
          after a failed submit stands out. The banner goes once the backend has checked this
          Product ID and not refused it; the field stays so it can still be changed. */}
      {variant.needsManualProductId && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {!isManualProductIdChecked && (
            <ManualProductIdInstructions conflictText={manualProductIdConflict} />
          )}
          <ManualProductIdField
            id={`NewCatalogDialog-productId${index}-input`}
            value={variant.manualProductIdCode}
            onChange={(manualProductIdCode) => onChange(index, { manualProductIdCode })}
            shortCode={findShortCode(formOptions, variant.formAlias)}
            formSelected={Boolean(variant.formAlias)}
            drugCode={drugCode}
            autoFocus={autoFocusProductId}
            conflictText={manualProductIdConflict}
          />
        </Stack>
      )}
    </Box>
  );
};

VariantRow.propTypes = {
  index: PropTypes.number.isRequired,
  variant: PropTypes.shape({
    strength: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    isCompoundingEligible: PropTypes.bool,
    needsManualProductId: PropTypes.bool,
    manualProductIdCode: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  formOptions: PropTypes.arrayOf(
    PropTypes.shape({
      formAlias: PropTypes.string,
      shortCode: PropTypes.string,
      units: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  loadingForms: PropTypes.bool.isRequired,
  formsError: PropTypes.bool.isRequired,
  submitted: PropTypes.bool.isRequired,
  manualProductIdConflict: PropTypes.string.isRequired,
  isManualProductIdChecked: PropTypes.bool.isRequired,
  autoFocusProductId: PropTypes.bool.isRequired,
  drugCode: PropTypes.string.isRequired,
};

// Visual shell matches e-scribe-ui's NewFormularyButton (navy header, Drug
// Name + Drug Code side by side, a "Variants" section with add/remove rows).
// The drug-code resolution below is the same registry-backed logic as
// StockManagement's AddCatalogDialog rather than e-scribe-ui's static
// regex-only version -- see that dialog's comments for why (kept in step
// with drugCodeMappingService instead of duplicated hardcoded lists).
const NewCatalogDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [drugName, setDrugName] = useState('');
  const [drugCode, setDrugCode] = useState('');
  const [variants, setVariants] = useState([emptyVariant()]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formsError, setFormsError] = useState(false);
  const [codeIndex, setCodeIndex] = useState(() => toDrugCodeIndex([]));
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codeManual, setCodeManual] = useState(false);
  const [codeFromRegistry, setCodeFromRegistry] = useState(false);
  const [mappedDrugName, setMappedDrugName] = useState('');
  // POM-6514: a Product ID field only appears on a variant createCatalogDrug could not generate
  // one for, so a successful automatic generation never asks for manual entry. takenProductIds
  // are the manual Product IDs the backend refused as already in the catalog; checkedProductIds
  // the ones it checked in the same request and did not -- their variants need no banner.
  const [takenProductIds, setTakenProductIds] = useState([]);
  const [checkedProductIds, setCheckedProductIds] = useState([]);
  const showManualProductId = variants.some(({ needsManualProductId }) => needsManualProductId);
  const firstManualProductIdIndex = variants.findIndex(
    ({ needsManualProductId }) => needsManualProductId
  );

  useEffect(() => {
    if (!open) return;

    setDrugName('');
    setDrugCode('');
    setVariants([emptyVariant()]);
    setSubmitted(false);
    setTakenProductIds([]);
    setCheckedProductIds([]);
    setCodeManual(false);
    setCodeFromRegistry(false);
    setMappedDrugName('');
    setCodeIndex(toDrugCodeIndex([]));
    setFormOptions([]);
    setFormsError(false);
    setLoadingCodes(true);
    setLoadingForms(true);

    listCatalogFormOptionsApi()
      .then(setFormOptions)
      .catch(() => setFormsError(true))
      .finally(() => setLoadingForms(false));

    listDrugCodeMappingsApi()
      .then((mappings) => setCodeIndex(toDrugCodeIndex(mappings)))
      .catch(() => setCodeManual(true))
      .finally(() => setLoadingCodes(false));
  }, [open]);

  useEffect(() => {
    if (!open || loadingCodes) return undefined;

    const trimmedName = drugName.trim();

    if (!trimmedName) {
      if (!codeManual) setDrugCode('');
      setCodeFromRegistry(false);
      setMappedDrugName('');
      return undefined;
    }

    const timer = setTimeout(() => {
      const {
        drugCode: resolvedCode,
        isExisting,
        canonicalDrugName,
      } = resolveDrugCodeForName(trimmedName, codeIndex);

      setCodeFromRegistry(isExisting);
      setMappedDrugName(canonicalDrugName);

      if (isExisting || !codeManual) setDrugCode(resolvedCode);
    }, DRUG_CODE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, drugName, codeManual, loadingCodes, codeIndex]);

  const handleVariantChange = (index, patch) => {
    setVariants((prev) =>
      prev.map((variant, i) => (i === index ? { ...variant, ...patch } : variant))
    );
  };

  const handleAddVariant = () => setVariants((prev) => [...prev, emptyVariant()]);

  const handleRemoveVariant = (index) => setVariants((prev) => prev.filter((_, i) => i !== index));

  const trimmedDrugCode = drugCode.trim();
  const drugCodeValid = drugCodeAllowedValueRegex.test(trimmedDrugCode);
  const drugCodeTaken = !codeFromRegistry && isDrugCodeTaken(trimmedDrugCode, codeIndex.takenCodes);
  const codeLocked = codeFromRegistry || (!codeManual && Boolean(trimmedDrugCode));
  const isDrugCodeMalformed = Boolean(trimmedDrugCode) && !drugCodeValid;
  const drugCodeError = drugCodeTaken || isDrugCodeMalformed || (submitted && !drugCodeValid);

  const getDrugCodeHelperText = () => {
    if (drugCodeTaken) return `${trimmedDrugCode} is already used — pick another`;
    if (loadingCodes) return 'Checking existing codes…';
    if (isDrugCodeMalformed) return DRUG_CODE_FORMAT_HINT;
    if (codeFromRegistry) return `Existing code for ${mappedDrugName}`;
    if (codeLocked) return 'Auto-generated from drug name';
    return '3 characters, used in the Product ID (e.g. FLX)';
  };

  const drugNameValid = drugNameAllowedValueRegex.test(drugName);

  const manualProductIds = variants.map((variant) =>
    resolveManualProductId(variant, findShortCode(formOptions, variant.formAlias))
  );
  const manualProductIdConflicts = variants.map((_, index) =>
    describeManualProductIdConflict({ productIds: manualProductIds, takenProductIds, index })
  );

  const submitBlockers = listCatalogSubmitBlockers({
    drugName,
    drugCode,
    isDrugCodeTaken: drugCodeTaken,
    isLoadingCodes: loadingCodes,
    variants: variants.map((variant, index) => ({
      ...variant,
      manualProductIdConflict: manualProductIdConflicts[index],
    })),
  });
  const canSave = submitBlockers.length === 0;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  // The dialog stays open with the input intact so a refused create (e.g.
  // CATALOG_ALREADY_EXISTS) can be corrected and resubmitted.
  const handleSaveError = (error) => {
    const onGenerationFailed = ({ failedVariantIndexes }) =>
      setVariants((prev) => flagVariantsNeedingManualProductId(prev, failedVariantIndexes));
    // An error naming no ids leaves every submitted manual Product ID suspect; one that names
    // some clears the rest it was checked with.
    const onProductIdTaken = (productIds) => {
      const submittedProductIds = compact(manualProductIds);
      const taken = productIds ?? submittedProductIds;

      setTakenProductIds((prev) => uniq([...prev, ...taken]));
      setCheckedProductIds((prev) =>
        uniq([...prev, ...submittedProductIds.filter((productId) => !taken.includes(productId))])
      );
    };
    const args = {
      error,
      showManualProductId,
      onGenerationFailed,
      onProductIdTaken,
      enqueueSnackbar,
    };

    if (!handleManualProductIdError(args)) dispatch(handleError({ error }));
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);

    const input = {
      drugName: drugName.trim(),
      drugCode: drugCode.trim().toUpperCase(),
      variants: variants.map(
        ({
          strength,
          formAlias,
          isCompoundingEligible,
          packSizeValue,
          packSizeUnit,
          needsManualProductId,
          manualProductIdCode,
        }) => ({
          strength: strength.trim(),
          formAlias,
          isCompoundingEligible,
          ...(packSizeValue.trim() ? { packSizeValue: packSizeValue.trim() } : {}),
          ...(packSizeUnit ? { packSizeUnit } : {}),
          ...(needsManualProductId
            ? toManualProductIdInput(manualProductIdCode, findShortCode(formOptions, formAlias))
            : {}),
        })
      ),
    };

    createCatalogDrugApi(input)
      // Waits for the reload before toasting/closing -- dispatching it without
      // awaiting let the success toast fire while the list was still loading.
      .then(() => dispatch(loadCatalog()))
      .then(() => {
        enqueueSnackbar('Catalog Created Successfully', { variant: 'success' });
        handleClose();
      })
      .catch(handleSaveError)
      .finally(() => setSaving(false));
  };

  // Only Cancel and the header's X close this -- a click on the backdrop
  // shouldn't discard a half-filled form.
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='md' fullWidth>
      <CatalogDialogHeader title='New Catalog' onClose={handleDialogClose} />

      <DialogContent>
        <Grid container columnSpacing={2}>
          <Grid item xs={8}>
            <TextField
              id='NewCatalogDialog-drugName-input'
              label='Drug Name'
              required
              fullWidth
              margin='normal'
              value={drugName}
              onChange={(event) => setDrugName(event.target.value)}
              inputProps={{
                onKeyDown: (event) => {
                  if (event.key.length === 1 && !drugNameAllowedCharRegex.test(event.key)) {
                    event.preventDefault();
                  }
                },
              }}
              error={(submitted && drugName.trim().length === 0) || !drugNameValid}
              helperText={drugNameValid ? RESERVED_HELPER_TEXT : drugNameAllowedCharsHint}
              FormHelperTextProps={HELPER_TEXT_PROPS}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              id='NewCatalogDialog-drugCode-input'
              label='Drug Code'
              required
              fullWidth
              margin='normal'
              value={drugCode}
              disabled={codeLocked || loadingCodes}
              onChange={(event) => {
                if (codeFromRegistry) return;
                setCodeManual(true);
                setDrugCode(event.target.value.toUpperCase().slice(0, 3));
              }}
              error={drugCodeError}
              helperText={getDrugCodeHelperText()}
              FormHelperTextProps={HELPER_TEXT_PROPS}
              InputProps={{
                inputProps: {
                  maxLength: 3,
                  style: { textTransform: 'uppercase', letterSpacing: 4 },
                },
                endAdornment:
                  codeLocked && !codeFromRegistry ? (
                    <InputAdornment position='end'>
                      <Tooltip title='Edit drug code'>
                        <IconButton
                          size='small'
                          aria-label='Edit drug code'
                          onClick={() => setCodeManual(true)}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Grid>
        </Grid>

        <Typography sx={{ mb: 1 }} color='primary' variant='h6' component='p'>
          Variants
        </Typography>

        {variants.map((variant, index) => (
          <VariantRow
            // Fresh, client-only rows with no server id yet -- index is the only
            // identity available, and re-ordering isn't supported (only append/remove).
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            index={index}
            variant={variant}
            onChange={handleVariantChange}
            onRemove={handleRemoveVariant}
            canRemove={variants.length > 1}
            formOptions={formOptions}
            loadingForms={loadingForms}
            formsError={formsError}
            submitted={submitted}
            manualProductIdConflict={manualProductIdConflicts[index]}
            isManualProductIdChecked={
              !manualProductIdConflicts[index] &&
              checkedProductIds.includes(manualProductIds[index])
            }
            autoFocusProductId={index === firstManualProductIdIndex}
            drugCode={trimmedDrugCode.toUpperCase()}
          />
        ))}

        <Button variant='outlined' fullWidth onClick={handleAddVariant}>
          Add more variants
        </Button>
      </DialogContent>

      <DialogActions>
        <CatalogSubmitBlockersHint blockers={submitBlockers} />
        <Button variant='outlined' onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          loading={saving}
          variant='contained'
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

NewCatalogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NewCatalogDialog;
